import { Button, Checkbox, Loading, Radio, Tooltip } from "@/components";
import { EVISIBILITY, QuizDto } from "@/types";
import { cn } from "@/utils";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuizResult } from "../hooks/useQuizDetail";
import { QuizModal } from "./QuizModal";

interface Props {
  quiz: QuizDto;
  closeModal?: () => void;
}

// Read-only option item component
const OptionItem = ({
  optionKey,
  content,
  isSelected,
  isCorrect,
  isMultipleChoice,
}: {
  optionKey: string;
  content: string;
  isSelected: boolean;
  isCorrect: boolean;
  isMultipleChoice?: boolean;
}) => {
  return (
    <div
      className={`flex items-start gap-3 p-4 border ${
        isCorrect && (isSelected || !isMultipleChoice)
          ? "border-success_lighter"
          : (isMultipleChoice && (isSelected || isCorrect)) ||
            (!isMultipleChoice && isSelected)
          ? "border-error_main"
          : "border-gray-200"
      } rounded-lg cursor-pointer hover:border-primary`}
    >
      <Tooltip side="bottom" content={`${optionKey}. ${content}`}>
        <div className="flex-1 text-sm truncate">
          <span className="mr-2">{optionKey}.</span>
          {content}
        </div>
      </Tooltip>
      {isMultipleChoice ? (
        <Checkbox
          color={isCorrect ? "green" : "red"}
          className="mt-0.5"
          checked={isSelected}
          onChange={() => {}}
        />
      ) : (
        <Radio
          color={isCorrect ? "green" : "red"}
          className="mt-0.5"
          checked={isSelected}
          onChange={() => {}}
        />
      )}
    </div>
  );
};

export const DoneQuizDetail = ({
  quiz: { user_quiz_id },
  closeModal,
}: Props) => {
  const navigate = useNavigate();

  const { data, isLoading } = useQuizResult(user_quiz_id || 0);

  const quiz = useMemo(() => {
    return {
      ...data?.data?.quizz,
      marks_obtained: data?.data?.quizz.marks_obtained || 0,
      passing_score: data?.data?.quizz.passing_score || 0,
      status: data?.data?.quizz?.status,
    } as QuizDto;
  }, [data]);

  const questions = useMemo(() => {
    return data?.data?.data || [];
  }, [data]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <form className="flex min-h-full gap-4">
      {/* left side */}
      <div className="flex-1 bg-white h-full rounded-lg shadow-shadow_light_card overflow-y-auto">
        <span className="flex flex-row items-center justify-between px-4 py-2.5 border-b border-gray-300 sticky top-0 bg-white z-10">
          <Tooltip side="bottom" content={quiz?.title || ""}>
            <p className="text-lg font-bold text-dark truncate max-w-full">
              {quiz?.title}
            </p>
          </Tooltip>
        </span>
        <div className="p-4 flex flex-col gap-8">
          {questions.map((question, index) => {
            // Check if question has multiple correct answers
            const isMultipleChoice =
              Array.isArray(question.correct_option) &&
              question.correct_option.length > 1;

            // Get user's selected answers
            const userAnswers = question.answers?.[0]?.selected_option || [];
            // Get correct answers
            const correctAnswers = question.correct_option || [];

            return (
              <div key={question.id ?? index} className="flex flex-col gap-4">
                <Tooltip
                  side="bottom"
                  content={`${index + 1}. ${question.question}`}
                >
                  <div className="font-semibold text-base truncate">
                    {index + 1}. {question.question}
                  </div>
                </Tooltip>
                <div className="grid grid-cols-2 gap-3">
                  {/* Option A */}
                  <OptionItem
                    optionKey="A"
                    content={question.option_a}
                    isSelected={userAnswers.includes("A")}
                    isCorrect={correctAnswers.includes("A")}
                    isMultipleChoice={isMultipleChoice}
                  />

                  {/* Option B */}
                  <OptionItem
                    optionKey="B"
                    content={question.option_b}
                    isSelected={userAnswers.includes("B")}
                    isCorrect={correctAnswers.includes("B")}
                    isMultipleChoice={isMultipleChoice}
                  />

                  {/* Option C */}
                  <OptionItem
                    optionKey="C"
                    content={question.option_c}
                    isSelected={userAnswers.includes("C")}
                    isCorrect={correctAnswers.includes("C")}
                    isMultipleChoice={isMultipleChoice}
                  />

                  {/* Option D */}
                  <OptionItem
                    optionKey="D"
                    content={question.option_d}
                    isSelected={userAnswers.includes("D")}
                    isCorrect={correctAnswers.includes("D")}
                    isMultipleChoice={isMultipleChoice}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* right side */}
      <div className="w-[23%] flex flex-col gap-4 max-h-[calc(100vh-96px)] sticky top-0">
        <div className="bg-white rounded-lg shadow-shadow_light_card overflow-y-auto">
          <span className="flex flex-row items-center justify-between px-4 py-2.5 border-b border-gray-300 sticky top-0 bg-white z-10">
            <p className="text-lg font-bold text-dark">回答済み</p>
          </span>
          <div
            className={cn(
              "py-8 text-center text-4xl font-bold",
              quiz.result === "PASSED"
                ? "text-success_lighter"
                : "text-error_main"
            )}
          >
            {quiz?.marks_obtained}/{quiz?.total_questions}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-shadow_light_card p-4 flex flex-col gap-2">
          <Button
            type="button"
            buttonType="primary"
            onClick={() => {
              closeModal?.();
              navigate("/");
            }}
          >
            トップページへ戻る
          </Button>
          <QuizModal
            quiz={quiz}
            onClose={() => closeModal?.()}
            trigger={
              <Button
                type="button"
                buttonType="text"
                disabled={quiz?.status == EVISIBILITY.PRIVATE}
              >
                最初から始める
              </Button>
            }
          />
        </div>
      </div>
    </form>
  );
};
